import logger from './logger.js';
import { getTaxonomyAPIURL } from './constants/api_urls.js';
import { PROD_ENV } from './constants/environments.js';
/**
 * Fetches regions from the API and transforms the data.
 *
 * This function makes a GET request to the marketplace-countries taxonomy endpoint using the middleware API.
 * It includes the required x-api-key header for authentication.
 * Before returning, it transforms the raw country data so that only countries with associated marketingRegions
 * are returned, grouped by region with country codes and region codes.
 *
 * @param {string} [env=PROD_ENV] - The environment to use for the API endpoint (e.g., production, UAT, dev).
 * @returns {Promise<Regions>} A promise that resolves to the transformed regions data.
 * @throws {Error} If the fetch request fails or the response is not OK.
 */
export async function fetchRegions(env = PROD_ENV) {
    // API configuration constants
    const PUBLIC_API_KEY = process.env.PUBLIC_TAXONOMY_API_KEY;
    const API_VERSION = 'latest';
    const MARKETPLACE_COUNTRIES_API_PATH = '/marketplace-countries';
    // Get the appropriate middleware URL for this environment
    const baseUrl = getTaxonomyAPIURL(env);
    const apiUrl = `${baseUrl}/${API_VERSION}${MARKETPLACE_COUNTRIES_API_PATH}`;
    // Set up abort controller for timeout (10 seconds)
    const controller = new AbortController();
    setTimeout(() => controller.abort(), 10000);
    if (!PUBLIC_API_KEY || !apiUrl) {
        logger.warn("Missing 'PUBLIC_API_KEY' or 'apiUrl' for taxonomy regions api call");
        return [];
    }
    const headers = {
        'Content-Type': 'application/json',
        'x-api-key': PUBLIC_API_KEY,
    };
    logger.log(`Fetching regions from API at: ${apiUrl}`);
    try {
        const response = await fetch(apiUrl, {
            method: 'GET',
            headers,
            mode: 'cors',
            signal: controller.signal,
        });
        if (!response.ok) {
            throw new Error(`Failed to fetch regions: ${response.status} - ${response.statusText}`);
        }
        const marketplaceCountries = await response.json();
        // Transform the data: filter countries with marketplaceRegions and group by region URI
        const transformedData = mapCountriesToRegions(marketplaceCountries);
        return transformedData;
    }
    catch (error) {
        logger.log(`Error fetching regions: ${error}`);
        throw error;
    }
}
/**
 * Maps the marketplace countries from taxonomy into their associated regions that user's can select
 * from the Region Selector.
 *
 * @param marketplaceCountries the raw list of marketplace countries from taxonomy
 * @returns the list of Regions for display to users
 */
function mapCountriesToRegions(marketplaceCountries) {
    const regionMap = new Map();
    // Filter countries that have marketplaceRegions and group by region URI
    marketplaceCountries.forEach(country => {
        // A country without a code can't be matched or queried, and would put undefined into the
        // region array (cached as null in localStorage)
        if (!country.countryCode) {
            return;
        }
        country.marketplaceRegions?.forEach(region => {
            const regionUri = region.uri;
            const regionLabel = region.label;
            if (!regionMap.has(regionUri)) {
                regionMap.set(regionUri, { label: regionLabel, uri: regionUri, countryCodes: [] });
            }
            regionMap.get(regionUri).countryCodes.push(country.countryCode);
        });
    });
    // Special regions that should use their label as the region value
    // These don't map to individual country codes for API queries.  
    // Note GLOBAL is now remove since it's using WW
    const SPECIAL_REGIONS = ['ASEAN', 'MEA', 'OTHERS'];
    // Transform to the required format
    const transformedData = Array.from(regionMap.values()).map(({ label, uri, countryCodes }) => {
        // For special regions (ASEAN, Global, etc.), use the label as the region value
        // For regular countries, use countryCodes
        const region = SPECIAL_REGIONS.includes(label) ? [label.toUpperCase()] : [...countryCodes];
        return {
            uri,
            label,
            region,
            countryCodes,
        };
    });
    return transformedData;
}
